import { Fragment, useEffect, useState } from 'react';
import {
  getOrders,
  updateOrder,
  issueInvoice,
  isMissingFunction,
  supabase,
} from '../services/adminApi';
import StatusBadge from '../components/StatusBadge';
import FormModal from '../components/FormModal';
import {
  buildPaymentRequestMessage,
  buildStatusMessage,
  waLink,
  normalisePhone,
} from '../../utils/whatsapp';
import { openInvoice } from '../../utils/invoice';
import { STORE, missingStoreDetails } from '../../utils/store';

const ORDER_STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];

// Statuses worth messaging a customer about
const STATUS_NOTIFY = ['confirmed', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_STATUSES = ['pending', 'paid', 'refunded', 'failed'];

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');

  // Order the "Generate invoice" dialog is open for, plus its inputs.
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [reference, setReference] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState('');

  const load = async () => {
    setLoading(true);
    const { data, error } = await getOrders();
    setOrders(data);
    setError(error);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (order, field, value) => {
    // optimistic update
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, [field]: value } : o))
    );
    const { error } = await updateOrder(order.id, { [field]: value });
    if (error) {
      alert(`Update failed: ${error.message}`);
      load();
      return;
    }

    // Cancelling returns the units to stock; un-cancelling takes them
    // back out. The function guards against doing either twice.
    if (field === 'order_status') {
      const wasCancelled = order.order_status === 'cancelled';
      const nowCancelled = value === 'cancelled';

      if (wasCancelled !== nowCancelled) {
        const { error: rpcError } = await supabase.rpc('apply_order_stock', {
          p_order_id: order.id,
          p_new_status: value,
        });
        if (rpcError) {
          console.error('Stock sync failed:', rpcError);
          alert(
            'Order status saved, but stock could not be re-synced. ' +
              'Check the quantity on the Products page.'
          );
        }
      }
    }

    // Offer to tell the customer straight away.
    if (field === 'order_status' && STATUS_NOTIFY.includes(value)) {
      const updated = { ...order, [field]: value };
      if (!normalisePhone(updated.customer_phone)) return;

      const send = window.confirm(
        `Order marked "${value}".\n\nSend a WhatsApp update to ${updated.customer_name} (${updated.customer_phone})?`
      );
      if (send) notifyCustomer(updated, value);
    }
  };

  /* Opens WhatsApp with a prefilled status message to the customer.
     Sending automatically would need the WhatsApp Business API; this is
     one click and uses the number captured at checkout. */
  const notifyCustomer = async (order, status) => {
    const phone = normalisePhone(order.customer_phone);
    if (!phone) {
      alert('This order has no valid customer phone number.');
      return;
    }

    window.open(waLink(phone, buildStatusMessage(order, status)), '_blank');

    await updateOrder(order.id, {
      whatsapp_sent: true,
      whatsapp_sent_at: new Date().toISOString(),
    });
    load();
  };

  /* Step one of the money flow: the order is in, nothing is paid yet,
     so send the customer the UPI details. No invoice goes with this —
     an invoice is a receipt and there is nothing to receipt yet. */
  const requestPayment = (order) => {
    const phone = normalisePhone(order.customer_phone);
    if (!phone) {
      alert('This order has no valid customer phone number.');
      return;
    }

    window.open(waLink(phone, buildPaymentRequestMessage(order)), '_blank');
  };

  const openInvoiceDialog = (order) => {
    setInvoiceFor(order);
    setReference(order.payment_reference || '');
    setIssueError('');
  };

  /* Step two: the money has landed. This one action records the
     payment and raises the invoice — they are the same event, so the
     database does both in a single statement. */
  const confirmInvoice = async () => {
    setIssuing(true);
    setIssueError('');

    const { data, error } = await issueInvoice(invoiceFor.id, {
      method: 'UPI',
      reference: reference.trim() || null,
    });

    setIssuing(false);

    if (error || !data) {
      /* The commonest cause by far is the migration not having been
         run. Say which file to run rather than passing PostgREST's
         "not in the schema cache" through to a shop owner. */
      setIssueError(
        isMissingFunction(error)
          ? 'Invoices are not set up on the database yet. Open Supabase → ' +
              'SQL Editor and run src/backend/sql/invoices.sql, then try again.'
          : `Could not raise the invoice: ${error?.message || 'unknown error'}`
      );
      return;
    }

    /* The row that comes back has the invoice number and paid date the
       database just assigned; the order_items are still only on the
       copy in state, and the invoice needs both. */
    const full = { ...invoiceFor, ...data };

    setOrders((prev) => prev.map((o) => (o.id === full.id ? full : o)));
    setInvoiceFor(null);

    const opened = openInvoice(full);
    if (!opened.ok) alert(opened.error);
  };

  // Reprint: nothing changes, the same document opens again.
  const viewInvoice = (order) => {
    const opened = openInvoice(order);
    if (!opened.ok) alert(opened.error);
  };

  const shown =
    filter === 'all' ? orders : orders.filter((o) => o.order_status === filter);

  const storeGaps = missingStoreDetails();

  if (loading) return <div className="admin-table-state">Loading orders…</div>;

  return (
    <div>
      <div className="page-head">
        <h1>Orders</h1>
        <div className="filter-pills">
          {['all', ...ORDER_STATUSES].map((s) => (
            <button
              key={s}
              className={`pill ${filter === s ? 'active' : ''}`}
              onClick={() => setFilter(s)}
            >
              {s === 'all' ? `All (${orders.length})` : s}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="admin-error">Failed to load orders: {error.message}</p>}

      {storeGaps.length > 0 && (
        <p className="admin-error">
          Invoices will print with placeholder details — the{' '}
          {storeGaps.join(' and ')} {storeGaps.length === 1 ? 'is' : 'are'} not
          set yet. Fill in <code>src/utils/store.js</code> before sending one to
          a customer.
        </p>
      )}

      {shown.length === 0 ? (
        <div className="admin-table-state">
          {filter === 'all'
            ? 'No orders yet. New WhatsApp orders from the shop will appear here.'
            : `No "${filter}" orders.`}
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '32px' }} />
                <th>Order #</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Order Status</th>
                <th>Payment</th>
                <th style={{ width: '210px' }}>Invoice</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <Fragment key={o.id}>
                  <tr
                    className="clickable"
                    onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  >
                    <td>{expanded === o.id ? '▾' : '▸'}</td>
                    <td className="cell-title">{o.order_number}</td>
                    <td>
                      <div className="cell-title">{o.customer_name}</div>
                      <div className="cell-sub">{o.customer_phone}</div>
                    </td>
                    <td className="cell-title">{inr(o.final_amount)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="status-select"
                        value={o.order_status || 'pending'}
                        onChange={(e) => setStatus(o, 'order_status', e.target.value)}
                      >
                        {ORDER_STATUSES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="status-select"
                        value={o.payment_status || 'pending'}
                        onChange={(e) =>
                          setStatus(o, 'payment_status', e.target.value)
                        }
                      >
                        {PAYMENT_STATUSES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    {/* The money flow, in the order it actually happens:
                        ask for payment, then receipt it. */}
                    <td onClick={(e) => e.stopPropagation()}>
                      {o.invoice_number ? (
                        <div className="action-buttons">
                          <button
                            className="btn-view"
                            onClick={() => viewInvoice(o)}
                            title={`Reprint invoice ${o.invoice_number}`}
                          >
                            🧾 {o.invoice_number}
                          </button>
                        </div>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="btn-view"
                            onClick={() => requestPayment(o)}
                            disabled={!normalisePhone(o.customer_phone)}
                            title={
                              normalisePhone(o.customer_phone)
                                ? `WhatsApp ${STORE.upiId} and the amount due`
                                : 'No valid phone number on this order'
                            }
                          >
                            Request payment
                          </button>
                          <button
                            className="btn-edit"
                            onClick={() => openInvoiceDialog(o)}
                            title="Payment received — raise the invoice"
                          >
                            Generate invoice
                          </button>
                        </div>
                      )}
                    </td>

                    <td className="cell-sub">{fmtDate(o.created_at)}</td>
                  </tr>

                  {expanded === o.id && (
                    <tr className="detail-row">
                      <td colSpan={8}>
                        <div className="order-detail">
                          <div className="order-detail-col">
                            <h4>Items</h4>
                            {(o.order_items || []).length === 0 ? (
                              <p className="cell-sub">No line items recorded.</p>
                            ) : (
                              <ul className="order-items">
                                {o.order_items.map((it) => (
                                  <li key={it.id}>
                                    <span>
                                      {it.product_name}{' '}
                                      <span className="cell-sub">
                                        ({it.planet}) × {it.quantity}
                                      </span>
                                    </span>
                                    <span>{inr(it.total_price)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="order-detail-col">
                            <h4>Details</h4>
                            <p>
                              <strong>Ship to:</strong>
                            </p>
                            <p className="ship-address">
                              {o.shipping_address || '—'}
                            </p>
                            <p>
                              <strong>Email:</strong> {o.customer_email || '—'}
                            </p>
                            <p>
                              <strong>Source:</strong> {o.source || '—'} ·{' '}
                              <strong>WhatsApp sent:</strong>{' '}
                              <StatusBadge
                                status={o.whatsapp_sent ? 'active' : 'inactive'}
                              />
                            </p>
                            <p>
                              <strong>Subtotal:</strong> {inr(o.total_amount)} ·{' '}
                              <strong>Shipping:</strong>{' '}
                              {inr(o.shipping_charges)} ·{' '}
                              <strong>Discount:</strong>{' '}
                              {inr(o.discount_amount)}
                              {o.coupon_code ? ` (${o.coupon_code})` : ''}
                            </p>
                            {o.invoice_number && (
                              <p>
                                <strong>Invoice:</strong> {o.invoice_number} ·{' '}
                                <strong>Paid:</strong> {fmtDate(o.paid_at)} via{' '}
                                {o.payment_method || 'UPI'}
                                {o.payment_reference
                                  ? ` · ref ${o.payment_reference}`
                                  : ''}
                              </p>
                            )}
                            <div className="notify-row">
                              <span className="notify-label">
                                Send update to customer:
                              </span>
                              <div className="action-buttons">
                                {STATUS_NOTIFY.map((s) => (
                                  <button
                                    key={s}
                                    className={
                                      s === 'cancelled' ? 'btn-delete' : 'btn-edit'
                                    }
                                    onClick={() => notifyCustomer(o, s)}
                                    disabled={!normalisePhone(o.customer_phone)}
                                    title={
                                      normalisePhone(o.customer_phone)
                                        ? `WhatsApp the customer that this order is ${s}`
                                        : 'No valid phone number on this order'
                                    }
                                  >
                                    {s === 'confirmed' && '✅ '}
                                    {s === 'shipped' && '📦 '}
                                    {s === 'delivered' && '🎉 '}
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <a
                              className="btn-view"
                              href={waLink(o.customer_phone, '')}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              💬 Open chat
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={!!invoiceFor}
        title="Payment received?"
        onClose={() => setInvoiceFor(null)}
      >
        <form
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            confirmInvoice();
          }}
        >
          <p className="hint">
            This raises invoice for <strong>{invoiceFor?.order_number}</strong> —{' '}
            {invoiceFor?.customer_name} — and marks it <strong>paid by UPI</strong>{' '}
            for <strong>{inr(invoiceFor?.final_amount)}</strong>. Only do this
            once the money is actually in your account.
          </p>

          <label>
            UPI reference <span style={{ fontWeight: 400 }}>(optional)</span>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. 462938471023"
              autoFocus
            />
            <p className="hint">
              The transaction ID from your UPI app. Printed on the invoice so
              the payment can be traced later. Leave blank to omit that line.
            </p>
          </label>

          {issueError && <p className="admin-error">{issueError}</p>}

          <div className="admin-modal-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setInvoiceFor(null)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={issuing}>
              {issuing ? 'Raising…' : 'Confirm payment & open invoice'}
            </button>
          </div>
        </form>
      </FormModal>
    </div>
  );
}
